import { forwardRef, isValidElement } from 'react';
import styled from '@emotion/styled';
import { css, useTheme } from '@emotion/react';

import { Tooltip } from '../Tooltip';
import type { Theme } from '../../theme';
import type { TypographyProps, TypographySize, TypographyWeight, TypographyColor } from './Typography.types';
import {
  VARIANT_DEFAULT_ELEMENTS,
  VARIANT_DEFAULT_SIZES,
  VARIANT_DEFAULT_WEIGHTS,
  VARIANT_DEFAULT_COLORS,
  VARIANT_DEFAULT_UPPERCASE,
} from './Typography.types';

/**
 * Get font size from theme, including the custom '2xl' size
 */
function getFontSize(size: TypographySize, theme: Theme): string {
  if (size === '2xl') {
    // Calculate 2xl as 1.5x the base font size (21px at default 14px base)
    const baseFontSize = theme.typography.baseFontSize;
    return `${Math.round(baseFontSize * 1.5)}px`;
  }
  return theme.typography.fontSize[size];
}

/**
 * Get color value from theme based on semantic color name
 */
function getColorValue(color: TypographyColor, theme: Theme): string {
  switch (color) {
    case 'primary':
      return theme.colors.text.primary;
    case 'secondary':
      return theme.colors.text.secondary;
    case 'muted':
      return theme.colors.text.muted;
    case 'inverse':
      return theme.colors.text.inverse;
    case 'error':
      return theme.colors.status.error;
    case 'success':
      return theme.colors.status.success;
    case 'warning':
      return theme.colors.status.warning;
    case 'info':
      return theme.colors.status.info;
    case 'highlight':
      return theme.colors.highlight;
    case 'inherit':
      return 'inherit';
    default:
      return theme.colors.text.primary;
  }
}

/**
 * Get font weight from theme
 */
function getFontWeight(weight: TypographyWeight, theme: Theme): number {
  return theme.typography.fontWeight[weight];
}

// Styled component for the text element
interface StyledTextProps {
  $fontSize: string;
  $fontWeight: number;
  $color: string;
  $uppercase: boolean;
  $truncate: boolean;
  $lines: number;
  $noWrap: boolean;
  $align?: 'left' | 'center' | 'right';
}

const StyledText = styled.span<StyledTextProps>`
  font-family: ${({ theme }) => (theme as Theme).typography.fontFamily};
  font-size: ${({ $fontSize }) => $fontSize};
  font-weight: ${({ $fontWeight }) => $fontWeight};
  color: ${({ $color }) => $color};
  text-transform: ${({ $uppercase }) => ($uppercase ? 'uppercase' : 'none')};
  text-align: ${({ $align }) => $align || 'inherit'};
  line-height: ${({ theme }) => (theme as Theme).typography.lineHeight.normal};
  margin: 0;
  padding: 0;

  ${({ $truncate, $lines, $noWrap }) =>
    $truncate
      ? $lines === 1
        ? css`
            display: block;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          `
        : css`
            display: -webkit-box;
            -webkit-line-clamp: ${$lines};
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;
          `
      : $noWrap
        ? css`
            white-space: nowrap;
          `
        : null}
`;

/**
 * Extract text content from ReactNode for tooltip
 */
function getTextContent(children: React.ReactNode): string {
  if (typeof children === 'string') {
    return children;
  }
  if (typeof children === 'number') {
    return String(children);
  }
  if (Array.isArray(children)) {
    return children.map(getTextContent).join('');
  }
  if (isValidElement(children) && children.props) {
    const props = children.props as { children?: React.ReactNode };
    return getTextContent(props.children);
  }
  return '';
}

/**
 * Typography component for consistent text styling across the application.
 *
 * Supports pre-defined variants with automatic styling:
 * - `heading`: Page/section titles (semibold, primary, lg)
 * - `label`: Labels and uppercase text (medium, muted, uppercase, xs)
 * - `caption`: Small helper text (muted, xs)
 * - `body`: Default paragraph text (regular, primary, sm)
 * - `error`: Error messages (error color, sm)
 *
 * Features:
 * - Size, weight, and color overrides
 * - Text truncation with automatic Tooltip
 * - Multi-line truncation support
 * - Semantic HTML elements
 *
 * @example
 * ```tsx
 * // Basic variants
 * <Typography variant="heading">Page Title</Typography>
 * <Typography variant="label">Field Label</Typography>
 * <Typography variant="caption">Helper text</Typography>
 * <Typography variant="body">Paragraph text</Typography>
 * <Typography variant="error">Error message</Typography>
 *
 * // Size and weight overrides
 * <Typography variant="heading" size="xl">Large Title</Typography>
 * <Typography variant="body" weight="semibold">Bold text</Typography>
 *
 * // Truncation with automatic tooltip
 * <Typography truncate>Very long text that will be truncated...</Typography>
 *
 * // Multi-line truncation
 * <Typography truncate lines={2}>
 *   Long text that spans multiple lines before being truncated...
 * </Typography>
 *
 * // Custom element
 * <Typography as="h1" variant="heading" size="2xl">Main Heading</Typography>
 * ```
 */
export const Typography = forwardRef<HTMLElement, TypographyProps>(
  (
    {
      children,
      variant = 'body',
      size,
      weight,
      color,
      uppercase,
      truncate = false,
      lines = 1,
      tooltipContent,
      disableTooltip = false,
      as,
      align,
      noWrap = false,
      style,
      className,
      dataTestId,
      dataId,
      ...rest
    },
    ref
  ) => {
    const theme = useTheme() as Theme;

    // Resolve effective values using variant defaults and overrides
    const effectiveSize = size ?? VARIANT_DEFAULT_SIZES[variant];
    const effectiveWeight = weight ?? VARIANT_DEFAULT_WEIGHTS[variant];
    const effectiveColor = color ?? VARIANT_DEFAULT_COLORS[variant];
    const effectiveUppercase = uppercase ?? VARIANT_DEFAULT_UPPERCASE[variant];
    const effectiveElement = as ?? VARIANT_DEFAULT_ELEMENTS[variant];

    // Get theme values
    const fontSize = getFontSize(effectiveSize, theme);
    const fontWeight = getFontWeight(effectiveWeight, theme);
    const colorValue = getColorValue(effectiveColor, theme);

    // Create the text element
    const textElement = (
      <StyledText
        as={effectiveElement as unknown as undefined}
        ref={ref as React.Ref<HTMLSpanElement>}
        $fontSize={fontSize}
        $fontWeight={fontWeight}
        $color={colorValue}
        $uppercase={effectiveUppercase}
        $truncate={truncate}
        $lines={lines}
        $noWrap={noWrap || truncate}
        $align={align}
        style={style}
        className={className}
        data-testid={dataTestId}
        data-id={dataId}
        {...rest}
      >
        {children}
      </StyledText>
    );

    // If truncation is enabled and tooltip is not disabled, wrap with Tooltip
    if (truncate && !disableTooltip) {
      const tooltipText = tooltipContent ?? getTextContent(children);
      
      return (
        <Tooltip
          content={tooltipText}
          onlyWhenOverflow
          position="bottom"
          maxWidth={400}
        >
          {textElement}
        </Tooltip>
      );
    }

    return textElement;
  }
);

Typography.displayName = 'Typography';

export default Typography;
